import React, { useMemo, useState } from "react";

function valueOrUnavailable(value) {
  return value === null || value === undefined || value === "" || value === "Unavailable"
    ? "Unavailable"
    : value;
}

const asArray = (value) => (Array.isArray(value) ? value : []);

const objectValuesAsArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value);
  return [];
};

const asObject = (value) => (value && typeof value === "object" && !Array.isArray(value) ? value : {});

const asSignalRecords = (value) => asArray(value).filter(
  (item) => item && typeof item === "object" && !Array.isArray(item) && typeof item.signal === "string" && item.signal.trim()
);

function normalizeWarnings(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value && typeof value === "object") return asArray(value.items).filter(Boolean);
  return [];
}

function displayNextLesson(progression) {
  if (progression?.next_lesson && progression.next_lesson !== "Unavailable") return progression.next_lesson;
  return "Locked";
}

function labelForSignal(signal, values) {
  const entry = values?.[signal];
  return entry?.label || String(signal || "").replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

function CompactRow({ label, value }) {
  const resolved = valueOrUnavailable(value);
  return (
    <div className="lp-admin-compact-row">
      <span className="lp-admin-compact-label">{label}</span>
      <span className="lp-admin-compact-value">{typeof resolved === "object" ? JSON.stringify(resolved) : String(resolved)}</span>
    </div>
  );
}

function formatTransformationValue(value) {
  if (typeof value === "number" && value > 0) return `+${value}`;
  return value;
}

function CompactList({ items, empty = "None" }) {
  const values = asArray(items).filter(Boolean);
  if (!values.length) return <span className="lp-admin-compact-muted">{empty}</span>;
  return (
    <ul className="lp-admin-compact-list">
      {values.slice(0, 12).map((item, index) => <li key={`${String(item)}-${index}`}>{String(item)}</li>)}
    </ul>
  );
}

export function buildCompactDetails(details) {
  const stages = asArray(details?.stages).filter((stage) => stage && typeof stage === "object");
  const byKey = Object.fromEntries(stages.map((stage) => [stage.stage_key, asObject(stage.values)]));
  const submission = asObject(byKey.frontend_submission);
  const context = asObject(byKey.load_context);
  const semantics = asObject(byKey.lesson_semantics);
  const signalStage = asObject(byKey.writing_signals);
  const scoreStage = asObject(byKey.overall_score);
  const dimensionStage = asObject(byKey.dimension_scores);
  const taskFitStage = asObject(byKey.task_fit);
  const progression = asObject(byKey.progression);
  const summary = asObject(byKey.evaluation_summary);
  const coach = asObject(byKey.coach_feedback);
  const llmStage = stages.find((stage) => stage.stage_key === "llm_interpretation") || {};
  const llm = asObject(llmStage.values);
  const accepted = asSignalRecords(signalStage.canonical_accepted ?? signalStage.accepted_signals);
  const rejected = asSignalRecords(signalStage.llm_rejected ?? signalStage.rejected_candidates);
  const lessonId = String(submission.lesson_id || context.lesson_id || "").toUpperCase();
  const expectedValue = signalStage.expected_signals;
  const expected = asArray(expectedValue).length
    ? asArray(expectedValue)
    : asArray(asObject(expectedValue)[lessonId]);
  const detectedNames = [...new Set(asArray(accepted).map((item) => String(item.signal || "").toUpperCase()).filter(Boolean))];
  const quotes = accepted.map((item) => ({ signal: item.signal, span: item.text, confidence: item.confidence, source: item.source }));
  const missing = asArray(signalStage.missing_expected_signals).length
    ? asArray(signalStage.missing_expected_signals)
    : expected.filter((signal) => !detectedNames.includes(signal));
  const strategy = asObject(context.evaluation_strategy);
  const weights = asObject(context.rubric_weights || asObject(semantics.resolved_evaluation_config).weights);
  const dimensions = asObject(dimensionStage.dimensions);
  const scoreConsistency = asObject(details?.consistency || scoreStage.score_consistency);
  const scoreComponents = asObject(details?.score_components || scoreStage.score_components);
  const normalizedFeedback = asObject(details?.feedback_output || asObject(summary.feedback_output));
  const normalizedMastery = asObject(details?.mastery);
  const normalizedSemantic = asObject(details?.semantic_evaluation);
  const normalizedLlm = asObject(details?.llm_evaluation);
  const normalizedSignals = asObject(details?.writing_signals);
  const masteryDimensions = asObject(scoreStage.mastery_dimensions);
  const stageStatus = String(llmStage.status || "unavailable");
  const warnings = normalizeWarnings(coach.consistency_warnings ?? asObject(byKey.frontend_result).consistency_warnings);
  const deterministicCandidateLines = Object.entries(asObject(signalStage.deterministic_candidates)).flatMap(([signal, payload]) =>
    objectValuesAsArray(asObject(payload).highlights).slice(0, 4).map((item) =>
      `${labelForSignal(signal)} — “${item.text || "Unavailable"}” — ${item.confidence ?? "confidence unavailable"}`
    )
  );
  const llmCandidates = asSignalRecords(signalStage.llm_verification_candidates);
  const llmAccepted = asSignalRecords(signalStage.llm_accepted);
  const llmCandidateLines = llmCandidates.map((item) =>
    `${labelForSignal(item.signal)} — “${item.text || "Unavailable"}" — ${item.confidence ?? "confidence unavailable"}`
  );

  return {
    submission: [
      ["Submission ID", (byKey.save_processing || {}).submission_id],
      ["Lesson ID", lessonId],
      ["Student ID", submission.student_id],
      ["Attempt", submission.attempt_number],
      ["Word count", submission.submission_word_count],
      ["Status", (byKey.final_save || {}).submission_status || "completed"],
    ],
    lesson: [
      ["Objective", context.objective],
      ["Mode", asObject(context.lesson_mode).mode || context.lesson_mode],
      ["Evaluation strategy", context.evaluation_strategy_template || strategy.template || strategy.id || strategy.name || strategy],
      ["Rubric weights", Object.entries(weights).map(([key, weight]) => `${labelForSignal(key)} ${weight}`).join(" · ")],
      ["Pass threshold", context.pass_threshold || scoreStage.threshold],
      ["Maximum attempts", context.maximum_attempts || 2],
    ],
    signals: {
      expected,
      detected: detectedNames.map((signal) => ({
        name: labelForSignal(signal),
        spans: accepted.filter((item) => String(item.signal || "").toUpperCase() === signal).slice(0, 3),
        source: accepted.find((item) => String(item.signal || "").toUpperCase() === signal)?.source || "deterministic",
      })),
      missing,
      evidence: quotes.slice(0, 12),
      candidates: asObject(signalStage.deterministic_candidates),
      deterministicCandidateLines,
      llmCandidateLines,
      llmCandidates,
      llmAccepted,
      rejected,
      detectorSource: signalStage.detector_source,
      writingSignalSource: signalStage.writing_signal_source || signalStage.detector_source,
      verificationSource: signalStage.verification_source,
      llmStatus: signalStage.llm_verification_status || "disabled",
    },
    llm: [
      ["Status", llm.status || stageStatus],
      ["Evaluation mode", normalizedLlm.evaluation_mode || llm.evaluation_mode || "Not provided"],
      ["Enabled", llm.enabled],
      ["Call attempted", llm.call_attempted],
      ["Call completed", llm.call_completed],
      ["Model", normalizedLlm.model || llm.model || "Not persisted"],
      ["Fallback used", llm.fallback],
      ["Rubric dimension source", llm.dimension_source],
      ["Lesson-objective effectiveness", normalizedSemantic.lesson_objective_effectiveness || llm.lesson_objective_effectiveness || "Not provided"],
      ["LLM rubric validation", llm.llm_rubric_validation],
      ["Mastery fallback reason", llm.mastery_fallback_reason],
      ["Task fit judgement", llm.semantic_task_fit_judgement],
      ["Confidence", llm.confidence],
      ["Evaluation merge scope", llm.evaluation_merge_scope || "Not provided"],
    ],
    taskFit: [
      ["Status", taskFitStage.status || "Not provided"],
      ["Judgement", taskFitStage.score_or_judgement || "Not produced by deterministic validation"],
      ["Evidence", taskFitStage.evidence || "Objective constraints validated"],
      ["Blocking findings", taskFitStage.blocking_findings || "None"],
      ["Source", taskFitStage.source],
    ],
    scoring: [
      ["Score", scoreStage.rounded_score],
      ["Dimensions", Object.entries(dimensions).map(([key, value]) => `${labelForSignal(key)} ${value}`).join(" · ")],
      ["Score consistency", scoreConsistency.status],
      ["Resolved weight profile", context.weight_profile],
      ["Resolved pass profile", context.pass_profile],
      ["Weighted base score", scoreStage.weighted_base_score ?? scoreConsistency.raw_weighted_score],
      ["Base rubric score", scoreComponents.base_rubric_score],
      ["Semantic dimension count", scoreComponents.semantic_dimension_count],
      ["Semantic average", scoreComponents.semantic_average],
      ["Semantic adjustment", scoreComponents.semantic_adjustment],
      ["Score after semantics", scoreComponents.score_after_semantics],
      ["Expected signal count", scoreComponents.expected_signal_count],
      ["Accepted expected signal count", scoreComponents.accepted_expected_signal_count],
      ["Signal coverage", scoreComponents.signal_coverage],
      ["Raw signal adjustment", scoreComponents.raw_signal_adjustment],
      ["Applied signal adjustment", scoreComponents.signal_adjustment],
      ["Score after signals / quality score", scoreComponents.score_after_signals ?? scoreComponents.quality_score],
      ["Quality mastery band", normalizedMastery.quality_band || scoreComponents.quality_mastery_band],
      ["Encouragement eligible bands", scoreComponents.encouragement_eligible_bands],
      ["Encouragement threshold", scoreComponents.encouragement_score_threshold ? `${scoreComponents.encouragement_score_threshold} (score < threshold)` : "Not configured"],
      ["Encouragement band eligible", scoreComponents.encouragement_band_eligible],
      ["Encouragement threshold eligible", scoreComponents.encouragement_threshold_eligible],
      ["Encouragement eligible", scoreComponents.encouragement_eligible],
      ["Encouragement applied", scoreComponents.encouragement_applied],
      ["Encouragement score before", scoreComponents.encouragement_score_before],
      ["Encouragement bonus configured", scoreComponents.encouragement_bonus_points_configured],
      ["Encouragement bonus applied", scoreComponents.encouragement_bonus_points_applied],
      ["Encouragement score after", scoreComponents.encouragement_score_after],
      ["Encouragement adjustment", scoreComponents.encouragement_adjustment],
      ["Mastery model enabled", scoreStage.mastery_model_enabled],
      ["Semantic dimension weighting", normalizedMastery.semantic_dimension_weighting || "Equal"],
      ["Mastery config source", scoreStage.mastery_config_source],
      ["Final mastery band (canonical)", normalizedMastery.final_band || asObject(byKey.mastery_band).mastery_band],
      ["Band range", normalizedMastery.band_range || "Not provided"],
      ["Mastery dimensions", Object.entries(masteryDimensions).map(([key, value]) => `${labelForSignal(key)} ${value}`).join(" · ")],
      ["Intra-band position", scoreStage.intra_band_position],
      ["Intra-band source", scoreStage.intra_band_source],
      ["Mastery validation status", normalizedMastery.validation_status || "Not separately validated"],
      ["Fallback reason", normalizedMastery.fallback_reason || "None"],
      ["Final score", scoreConsistency.final_score],
      ["Score difference", scoreConsistency.difference],
      ["Transformation", scoreConsistency.transformation_name],
      ["Transformation value", formatTransformationValue(scoreConsistency.transformation_value)],
      ["Explanation", scoreConsistency.explanation],
      ["Published score", normalizedMastery.published_score ?? scoreComponents.published_score ?? scoreConsistency.final_score],
      ["Final mastery band", normalizedMastery.final_band || scoreComponents.final_mastery_band || asObject(byKey.mastery_band).mastery_band],
      ["Mastery status", asObject(byKey.mastery_band).mastery_status],
      ["Mastery fallback used", normalizedMastery.fallback_used],
    ],
    progression: [
      ["Passed", progression.passed],
      ["Retry allowed", progression.retry_allowed],
      ["Lesson completed", progression.lesson_completed],
      ["Next action", progression.next_action],
      ["Next lesson", displayNextLesson(progression)],
      ["Decision", progression.decision_reason],
    ],
    feedback: [
      ["Overall summary", normalizedFeedback.overall_summary || summary.final_rendered_summary || "Not generated"],
      ["Summary status", normalizedFeedback.summary_status || summary.summary_status || "Not generated"],
      ["Summary source", normalizedFeedback.summary_source || summary.summary_source || "Not persisted"],
      ["Task completion", normalizedFeedback.task_completion || asObject(summary.dimension_summaries).task_completion || "Not generated"],
      ["Clarity", normalizedFeedback.clarity || asObject(summary.dimension_summaries).clarity || "Not generated"],
      ["Creativity", normalizedFeedback.creativity || asObject(summary.dimension_summaries).creativity || "Not generated"],
      ["Coach feedback", normalizedFeedback.coach_feedback || coach.final_feedback?.coach_feedback_paragraph || "Not generated"],
      ["Progression state", coach.progression_state],
    ],
    warnings,
    summary: {
      mastery: `${normalizedMastery.final_band || asObject(byKey.mastery_band).mastery_band || "Not provided"} / ${normalizedMastery.intra_band_position || asObject(byKey.inter_band).inter_band_position || "Not provided"}`,
      llmStatus: normalizedLlm.status || llm.status || stageStatus,
      signalStatus: signalStage.llm_verification_status || "disabled",
      acceptedCount: normalizedSignals.accepted_evidence_span_count ?? accepted.length,
      acceptedUniqueTypeCount: normalizedSignals.accepted_unique_signal_type_count ?? detectedNames.length,
      expectedTypeCount: normalizedSignals.expected_signal_type_count ?? expected.length,
      signalCoverage: normalizedSignals.coverage ?? signalStage.signal_coverage,
      rejectedCount: rejected.length,
      warningCount: warnings.length,
    },
  };
}

export default function AdminEvaluationDetails({ details, currentUser }) {
  const [open, setOpen] = useState(false);
  const compact = useMemo(() => buildCompactDetails(details), [details]);
  if (String(currentUser?.role || "").trim().toLowerCase() !== "admin") return null;
  if (!details || typeof details !== "object" || Array.isArray(details)) return null;
  const hasStages = asArray(details.stages).length > 0;

  return (
    <section className="lp-box lp-admin-details" aria-label="Admin evaluation details">
      <div className="lp-coach-feedback-head">
        <div>
          <h3 className="lp-coach-feedback-title">Details</h3>
          <p className="lp-coach-feedback-copy">Admin evaluation diagnostics for this submission</p>
        </div>
        <div className="lp-admin-compact-summary" aria-label="Admin diagnostic summary">
          <span>{hasStages ? `Mastery: ${compact.summary.mastery}` : "Diagnostics data incomplete"}</span>
          <span>LLM semantics: {compact.summary.llmStatus}</span>
          <span>Signal verification: {compact.summary.signalStatus}</span>
          <span>Signals: {compact.summary.acceptedCount} evidence spans, {compact.summary.acceptedUniqueTypeCount} unique types, {compact.summary.expectedTypeCount} expected</span>
          <span>Warnings: {compact.summary.warningCount}</span>
        </div>
        <button type="button" className="lp-btn lp-secondary-action" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
          {open ? "Collapse" : "Expand"}
        </button>
      </div>
      {open ? (
        <div className="lp-admin-compact-body">
          {!hasStages ? <p className="lp-admin-compact-muted">Diagnostics data incomplete</p> : null}
          {hasStages ? <>
          <section><h4>Submission</h4>{asArray(compact.submission).map(([label, value]) => <CompactRow key={label} label={label} value={value} />)}</section>
          <section><h4>Lesson configuration</h4>{asArray(compact.lesson).map(([label, value]) => <CompactRow key={label} label={label} value={value} />)}</section>
          <section>
            <h4>LLM semantic evaluation</h4>
            {asArray(compact.taskFit).map(([label, value]) => <CompactRow key={`task-fit-${label}`} label={`Deterministic task-fit: ${label}`} value={value} />)}
            {asArray(compact.llm).map(([label, value]) => <CompactRow key={label} label={label} value={value} />)}
          </section>
          <section>
            <h4>Evidence and Writing Signals</h4>
            <CompactRow label="Expected" value={asArray(compact.signals.expected).map((signal) => labelForSignal(signal)).join(", ") || "None"} />
            <div className="lp-admin-compact-label">Detected</div>
            <CompactList items={asArray(compact.signals.detected).map((item) => `${item.name} — ${(asArray(item.spans)[0]?.text || "Unavailable")} — ${item.source}`)} />
            <div className="lp-admin-compact-label">Deterministic candidates</div>
            <CompactList items={compact.signals.deterministicCandidateLines} />
            <div className="lp-admin-compact-label">LLM verification candidates</div>
            <CompactList items={compact.signals.llmCandidateLines} />
            <CompactRow label="Missing" value={asArray(compact.signals.missing).map((signal) => labelForSignal(signal)).join(", ") || "None"} />
            <div className="lp-admin-compact-label">Accepted evidence spans</div>
            <CompactList items={asArray(compact.signals.evidence).map(({ signal, span, confidence, source }) => `${labelForSignal(signal)} — “${span}” — ${source || "canonical"} — ${confidence ?? "confidence unavailable"}`)} />
            <CompactRow label="Deterministic accepted" value={asArray(compact.signals.detected).filter((item) => item.source === "deterministic").map((item) => item.name).join(", ") || "None"} />
            <CompactRow label="LLM accepted" value={asArray(compact.signals.llmAccepted).map((item) => labelForSignal(item.signal)).join(", ") || "None"} />
            <div className="lp-admin-compact-label">Rejected candidates</div>
            <CompactList items={asArray(compact.signals.rejected).map((item) => `${item.text || "Unavailable"} — ${item.reason || "rejected"}`)} />
            <CompactRow label="Writing-signal source" value={compact.signals.writingSignalSource} />
            <CompactRow label="Writing-signal detector source" value={compact.signals.detectorSource} />
            <CompactRow label="Verification source" value={compact.signals.verificationSource} />
            <CompactRow label="LLM verification" value={compact.signals.llmStatus} />
          </section>
          <section><h4>Scoring and mastery</h4>{asArray(compact.scoring).map(([label, value]) => <CompactRow key={label} label={label} value={value} />)}</section>
          <section><h4>Progression</h4>{asArray(compact.progression).map(([label, value]) => <CompactRow key={label} label={label} value={value} />)}</section>
          <section><h4>Feedback output</h4>{asArray(compact.feedback).map(([label, value]) => <CompactRow key={label} label={label} value={value} />)}</section>
          <section><h4>Consistency warnings</h4><CompactList items={compact.warnings} empty="None" /></section>
          </> : null}
        </div>
      ) : null}
    </section>
  );
}
